import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPreviewLink,
  getDesign,
  getMarkets,
  getStatus,
  publishConfig,
  themeEmbedDeepLink,
  toastPublishResult,
  updateDesign,
} from "../lib/safegeo-api";
import {
  countryLabel,
  hasFeature,
  planLabel,
} from "../lib/format";
import { fieldChecked, fieldValue } from "../lib/polaris";
import { queryKeys } from "../lib/query-keys";
import { snapshotEqual, useSaveBar } from "../lib/save-bar";
import { UpgradeNowButton } from "../components/UpgradeNowButton";
import { SafePage } from "../components/SafePage";
import { PhaseBadge, phaseFromStatus } from "../components/PhaseBadge";
import {
  ErrorBanner,
  InfoBanner,
  WarningBanner,
} from "../components/PageBanner";
import { AppLink } from "../components/AppLink";
import { AppButton } from "../components/AppButton";
import { SaveBarHost } from "../components/SaveBarHost";
import { PopupPreview } from "../components/PopupPreview";
import { CountrySelect } from "../components/CountrySelect";
import "../styles/preview-stage.css";
import "./popup-page.css";
import { pickVisitorCountry } from "../lib/preview-country";

const API_KEY = import.meta.env.VITE_SHOPIFY_API_KEY || "";
const SAVE_BAR_ID = "safegeo-popup-save";
const DEFAULT_HELPER =
  "We can only ship to addresses in the country you choose.";

/** v9 §33: Content · Selector · Style */
type SettingsTab = "content" | "selector" | "style";

/** v9 Style: Corner / Bottom bar / Center */
const PRESETS = [
  {
    id: "corner",
    label: "Corner",
    hint: "Small card in the corner",
  },
  {
    id: "bar",
    label: "Bottom bar",
    hint: "Thin bar along the bottom edge",
  },
  {
    id: "center",
    label: "Center",
    hint: "Centered modal with dimmed background",
  },
] as const;

const COLOR_PRESETS = [
  { id: "#303030", label: "Brand black" },
  { id: "#005BD3", label: "Blue" },
  { id: "#0C7A4B", label: "Green" },
  { id: "#C2410C", label: "Warm" },
  { id: "#E3E3E3", label: "Light" },
] as const;

type Draft = {
  style: string;
  matchTheme: boolean;
  sameForAll: boolean;
  language: string;
  title: string;
  reason1: string;
  reason2: string;
  button: string;
  stayLink: string;
  buttonColor: string;
  showBlur: boolean;
  buttonStyle: "rounded" | "square";
  buttonAlign: "left" | "stretch" | "right";
  titleSize: number;
  textSize: number;
  closeColor: string;
  ctaTextColor: string;
  textColor: string;
  cardBg: string;
  css: string;
  /** Market selector (v9 §32–33). Country always on. */
  showCurrency: boolean;
  showLanguage: boolean;
  showFlags: boolean;
  countryOrder: "alpha" | "market";
};

function remapLegacyButton(v: string) {
  if (
    v === "Switch" ||
    v === "Shop now" ||
    v === "Show Now" ||
    v === "Shop in {currency}" ||
    /^Shop in [A-Z]{3}$/.test(v)
  ) {
    return "Continue";
  }
  return v;
}
function remapLegacyStay(v: string) {
  if (
    v === "Not now" ||
    v === "Stay here" ||
    v === "Stay in {currency}" ||
    /^Stay in [A-Z]{3}$/.test(v) ||
    /^Keep shopping in [A-Z]{3}$/.test(v) ||
    v === "Keep shopping in {currency}"
  ) {
    return "Keep shopping in {currency}";
  }
  return v;
}
function remapLegacyTitle(v: string) {
  if (
    v === "Shopping from {country}?" ||
    v === "Are you in {country}?" ||
    /^Are you in .+\?$/.test(v)
  ) {
    return "Are you in the right place?";
  }
  return v;
}
function remapLegacyReason(v: string) {
  if (
    v === "Shop in your currency" ||
    v === "Please select your shipping country." ||
    v === "Prices and shipping will match {country}." ||
    /^Prices and shipping will match /.test(v)
  ) {
    return "Choose your shipping country and language.";
  }
  return v;
}

export function PopupPage() {
  const qc = useQueryClient();
  const designQ = useQuery({ queryKey: queryKeys.design, queryFn: getDesign });
  const statusQ = useQuery({ queryKey: queryKeys.status, queryFn: getStatus });
  const marketsQ = useQuery({
    queryKey: queryKeys.markets,
    queryFn: getMarkets,
  });

  const [draft, setDraft] = useState<Draft>({
    style: "center",
    matchTheme: true,
    sameForAll: true,
    language: "auto",
    title: "Are you in the right place?",
    reason1: "Choose your shipping country and language.",
    reason2: DEFAULT_HELPER,
    button: "Continue",
    stayLink: "Keep shopping in {currency}",
    buttonColor: "#303030",
    showBlur: true,
    buttonStyle: "rounded",
    buttonAlign: "stretch",
    titleSize: 20,
    textSize: 14,
    closeColor: "#5C5F62",
    ctaTextColor: "#FFFFFF",
    textColor: "#000000",
    cardBg: "#FFFFFF",
    css: "",
    showCurrency: true,
    showLanguage: false,
    showFlags: true,
    countryOrder: "alpha",
  });
  const [saved, setSaved] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [previewCode, setPreviewCode] = useState("CA");
  const [settingsTab, setSettingsTab] = useState<SettingsTab>("content");
  /** Message for Market | Store (store copy until API = local). */
  const [msgFor, setMsgFor] = useState<"market" | "store">("market");
  const [storeCopy, setStoreCopy] = useState<{
    title: string;
    reason1: string;
    reason2: string;
    button: string;
  } | null>(null);
  /** Helper text optional - empty = off. */
  const [helperOn, setHelperOn] = useState(true);
  const [savedHelperOn, setSavedHelperOn] = useState(true);
  const [previewLang, setPreviewLang] = useState<"en" | "fr">("en");

  useEffect(() => {
    const d = designQ.data;
    if (!d) return;
    const raw = d.style || "corner";
    const helperRaw =
      d.reason_2 == null
        ? null
        : String(d.reason_2).trim();
    const helperEnabled = helperRaw == null || helperRaw.length > 0;
    let helper =
      helperRaw && helperRaw.length > 0 ? helperRaw : DEFAULT_HELPER;
    if (
      helper ===
      "We’ll show local prices. We can only ship to addresses in the country you choose."
    ) {
      helper = DEFAULT_HELPER;
    }
    const next: Draft = {
      style: raw === "popup" ? "center" : raw,
      matchTheme: d.match_theme !== false,
      sameForAll: d.same_for_all !== false,
      language: "auto",
      title: remapLegacyTitle(d.title || "Are you in the right place?"),
      reason1: remapLegacyReason(
        d.reason_1 ||
          d.message ||
          "Choose your shipping country and language.",
      ),
      reason2: helperEnabled ? helper : "",
      button: remapLegacyButton(d.button || "Continue"),
      stayLink: remapLegacyStay(
        d.stay_link || "Keep shopping in {currency}",
      ),
      buttonColor: d.button_color || "#303030",
      showBlur: d.show_blur !== false,
      buttonStyle: d.button_style === "square" ? "square" : "rounded",
      buttonAlign:
        d.button_align === "left" || d.button_align === "right"
          ? d.button_align
          : "stretch",
      titleSize: Number(d.title_size) || 20,
      textSize: Number(d.text_size) || 14,
      closeColor: d.close_color || "#5C5F62",
      ctaTextColor: d.cta_text_color || "#FFFFFF",
      textColor: d.text_color || "#000000",
      cardBg: d.card_bg || "#FFFFFF",
      css: d.css || "",
      showCurrency: d.show_currency !== false,
      showLanguage: d.show_language === true,
      showFlags: d.show_flag !== false,
      countryOrder: d.country_order === "market" ? "market" : "alpha",
    };
    setHelperOn(helperEnabled);
    setSavedHelperOn(helperEnabled);
    setDraft(next);
    setSaved({
      ...next,
      reason2: helperEnabled ? helper : "",
    });
  }, [designQ.data]);

  const dirty =
    !!saved &&
    (!snapshotEqual(draft, saved) || helperOn !== savedHelperOn);
  // Settle a batch of edits before prompting Save (less noisy than per keystroke).
  useSaveBar(SAVE_BAR_ID, dirty, { debounceMs: 1200 });

  const plan = planLabel(statusQ.data?.plan);
  const canCss = hasFeature(
    statusQ.data?.entitlements?.features,
    "customCss",
    { plan: statusQ.data?.plan, planKey: statusQ.data?.plan_key },
  );
  const multiUnlocked = !!statusQ.data?.multi_store_unlocked;
  // Same order as Markets page: live Shopify Markets first, then saved settings.
  const countries =
    marketsQ.data?.countries?.length
      ? marketsQ.data.countries
      : statusQ.data?.settings?.market_countries || [];
  const homeCode = (countries[0] || "").toUpperCase();

  useEffect(() => {
    if (!countries.length) return;
    setPreviewCode((cur) => pickVisitorCountry(countries, cur));
  }, [countries.join("|")]);

  const previewLabel = countryLabel(previewCode);
  const homeLabel = homeCode ? countryLabel(homeCode) : "your primary market";
  const currencies = {
    ...(statusQ.data?.settings?.market_currencies || {}),
    ...(marketsQ.data?.currencies || {}),
  };
  const parseLangIds = (raw: string | undefined): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    String(raw || "")
      .split(/[,]/)
      .forEach((part) => {
        const id = part.toLowerCase().split(/[-_]/)[0].trim();
        if (!id || seen.has(id)) return;
        seen.add(id);
        out.push(id);
      });
    return out;
  };
  /** Prefer the richer locale list when merging cached settings + live Markets. */
  const mergeLanguageMaps = (
    a: Record<string, string>,
    b: Record<string, string>,
  ): Record<string, string> => {
    const out: Record<string, string> = { ...a };
    for (const [cc, raw] of Object.entries(b)) {
      const aIds = parseLangIds(out[cc]);
      const bIds = parseLangIds(raw);
      out[cc] = (bIds.length >= aIds.length ? bIds : aIds).join(",");
    }
    return out;
  };
  /** Market locales (primary[,alt…]) - keep the fuller of cache vs live sync. */
  const languageMap = mergeLanguageMaps(
    statusQ.data?.settings?.market_languages || {},
    marketsQ.data?.languages || {},
  );
  const langLabel = (id: string): string => {
    try {
      const name =
        new Intl.DisplayNames([id], { type: "language" }).of(id) || id;
      return name.charAt(0).toUpperCase() + name.slice(1);
    } catch {
      return id === "en" ? "English" : id;
    }
  };
  const storeLangIds = (() => {
    const seen = new Set<string>();
    const out: string[] = [];
    Object.values(languageMap).forEach((raw) => {
      parseLangIds(raw).forEach((id) => {
        if (seen.has(id)) return;
        seen.add(id);
        out.push(id);
      });
    });
    return out;
  })();
  const previewCurrency =
    currencies[previewCode] ||
    (previewCode === "US" ? "USD" : previewCode === "CA" ? "CAD" : "USD");
  const shop = statusQ.data?.shop_domain;
  const embedEditorUrl =
    shop && API_KEY ? themeEmbedDeepLink(shop, API_KEY) : null;
  const embedOn = !!statusQ.data?.embed_active;
  const workerOk = statusQ.data?.worker_configured !== false;
  const nestPreviewOk = statusQ.data?.preview_verify_configured !== false;
  const canOpenStore = workerOk || nestPreviewOk;
  const previewIsHome =
    !!homeCode && previewCode.toUpperCase() === homeCode;
  const marketCodes = countries.map((c) => String(c).toUpperCase().slice(0, 2));
  const previewInMarkets = marketCodes.includes(
    previewCode.toUpperCase().slice(0, 2),
  );
  /** Non-home Market country recommended for seeing the ask (e.g. CA). */
  const askPreviewCode = pickVisitorCountry(countries, "CA");
  const askPreviewLabel = countryLabel(askPreviewCode);
  /** Primary or out-of-Market → Open storefront won’t show the ask. */
  const openStoreNoAsk = previewIsHome || !previewInMarkets;
  const fillPreview = (s: string, currency: string) =>
    s
      .replace(/\{currency\}/gi, currency)
      .replace(/\{country\}/gi, countryLabel(previewCode));
  const previewTitle = fillPreview(
    draft.title || "Are you in the right place?",
    previewCurrency,
  );
  const previewMessage = fillPreview(
    draft.reason1 || "Choose your shipping country and language.",
    previewCurrency,
  );
  const previewGo = fillPreview(
    remapLegacyButton(draft.button || "Continue"),
    previewCurrency,
  );
  const visitorCc = (
    previewInMarkets ? previewCode : askPreviewCode
  )
    .toUpperCase()
    .slice(0, 2);
  const chooserCountries = (() => {
    const rows = countries.map((code) => ({
      code: String(code).toUpperCase().slice(0, 2),
      label: countryLabel(code),
    }));
    const pref = rows.find((r) => r.code === visitorCc);
    const rest = rows.filter((r) => r.code !== visitorCc);
    if (draft.countryOrder === "alpha") {
      rest.sort((a, b) => a.label.localeCompare(b.label));
    }
    return pref ? [pref, ...rest] : rest;
  })();
  /** Languages of the preview country / store (§32.2). */
  const chooserLanguages = (() => {
    const forCc = parseLangIds(languageMap[visitorCc]);
    const ids = forCc.length >= 2 ? forCc : storeLangIds;
    return ids.map((id) => ({ id, label: langLabel(id) }));
  })();
  /**
   * Soft hint when Shopify only reports one locale. Still allow the toggle so
   * merchants can turn off a stuck-on storefront Language field, and so Preview
   * follows Show language when Markets sync catches up.
   */
  const languageSelectHint = storeLangIds.length < 2;
  const helperPreview =
    helperOn && draft.reason2.trim()
      ? fillPreview(draft.reason2, previewCurrency)
      : undefined;
  // Mobile storefront maps center/corner → sheet; Admin mock should match.
  const previewStyle =
    device === "mobile" &&
    (draft.style === "center" || draft.style === "corner")
      ? "sheet"
      : draft.style;

  function patch(p: Partial<Draft>) {
    setDraft((d) => ({ ...d, ...p }));
  }

  function discard() {
    if (saved) setDraft(saved);
    setErr(null);
  }

  async function openStorePreview() {
    setBusy(true);
    setErr(null);
    try {
      const link = await createPreviewLink(previewCode);
      window.open(link.url, "_blank", "noopener,noreferrer");
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const r1 = draft.reason1.slice(0, 120);
      const r2 = helperOn ? draft.reason2.slice(0, 280) : "";
      await updateDesign({
        style: draft.style,
        language: "auto",
        match_theme: draft.matchTheme,
        same_for_all: draft.sameForAll,
        title: draft.title.slice(0, 80) || "Are you in the right place?",
        reason_1: r1,
        reason_2: r2,
        message: r1 || "Choose your shipping country and language.",
        button: draft.button.slice(0, 40) || "Continue",
        stay_link: draft.stayLink.slice(0, 40),
        button_color: draft.buttonColor,
        show_flag: draft.showFlags,
        show_currency: draft.showCurrency,
        show_language: draft.showLanguage === true,
        country_order: draft.countryOrder,
        show_blur: draft.showBlur,
        button_style: draft.buttonStyle,
        button_align: draft.buttonAlign,
        title_size: draft.titleSize,
        text_size: draft.textSize,
        close_color: draft.closeColor,
        cta_text_color: draft.ctaTextColor,
        text_color: draft.textColor,
        card_bg: draft.cardBg,
        corner_radius: draft.buttonStyle === "square" ? 0 : 8,
        ...(canCss ? { css: draft.css.slice(0, 10000) } : {}),
      });
      let published = null;
      try {
        published = await publishConfig();
      } catch {
        /* design saved */
      }
      const pub = toastPublishResult(
        published,
        "Saved and published to your storefront.",
      );
      if (!pub.ok && pub.message) setErr(pub.message);
      const next = {
        ...draft,
        language: "auto",
        title: draft.title.slice(0, 80) || "Are you in the right place?",
        reason1: r1,
        reason2: r2,
      };
      setDraft(next);
      setSaved(next);
      setSavedHelperOn(helperOn);
      void qc.invalidateQueries({ queryKey: ["safegeo"] });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  }

  const phase = phaseFromStatus({
    published: !!statusQ.data?.last_published_at,
    paused: !!statusQ.data?.settings?.paused,
  });

  return (
    <SafePage
      screen="popup"
      heading="Popup"
      accessory={<PhaseBadge phase={phase} />}
      secondaryActions={
        <AppButton
          slot="secondary-actions"
          disabled={busy}
          onClick={() => void openStorePreview()}
        >
          Open store in test
        </AppButton>
      }
    >
      <SaveBarHost
        id={SAVE_BAR_ID}
        saving={busy}
        onSave={() => void save()}
        onDiscard={discard}
      />
{err ? (
        <ErrorBanner heading="Couldn’t save popup">{err}</ErrorBanner>
      ) : null}

      <s-box paddingBlockEnd="base">
        <s-stack
          direction="inline"
          gap="base"
          alignItems="center"
          justifyContent="space-between"
        >
          <s-stack direction="inline" gap="small" alignItems="center">
            <s-badge tone={embedOn ? "success" : "warning"}>
              {embedOn ? "App embed is on" : "App embed is off"}
            </s-badge>
            <s-text color="subdued">
              Ask first / Automatic is set per country in Market and Store
              redirect
            </s-text>
          </s-stack>
          {!embedOn && embedEditorUrl ? (
            <AppButton variant="primary" href={embedEditorUrl}>
              Turn on in theme
            </AppButton>
          ) : null}
        </s-stack>
      </s-box>

      {multiUnlocked ? (
        <s-section heading="All stores">
          <s-checkbox
            label="Use the same popup on all stores"
            checked={draft.sameForAll}
            onChange={() => patch({ sameForAll: !draft.sameForAll })}
          />
          <s-paragraph color="subdued">
            When on, Publish to connected stores copies this popup to every
            linked shop.
          </s-paragraph>
        </s-section>
      ) : null}

      <div className="sg-popup-editor">
        {/* First in DOM → above the fold on narrow; order:2 → right column on wide */}
        <div className="sg-popup-preview">
          <s-box
            borderWidth="base"
            borderRadius="base"
            padding="base"
            background="subdued"
          >
            <s-stack gap="base">
              <s-stack
                direction="inline"
                gap="base"
                alignItems="center"
                justifyContent="space-between"
              >
                <s-text type="strong">Preview</s-text>
                <s-stack direction="inline" gap="small-100">
                  <s-button
                    type="button"
                    variant={device === "desktop" ? "primary" : "tertiary"}
                    icon="desktop"
                    accessibilityLabel="Desktop preview"
                    onClick={() => setDevice("desktop")}
                  />
                  <s-button
                    type="button"
                    variant={device === "mobile" ? "primary" : "tertiary"}
                    icon="mobile"
                    accessibilityLabel="Mobile preview"
                    onClick={() => setDevice("mobile")}
                  />
                </s-stack>
              </s-stack>
              <CountrySelect
                label="Preview as shopper from"
                value={previewCode}
                onChange={setPreviewCode}
                prefer={countries}
                details={`Primary market stays ${homeLabel}${currencies[homeCode] ? ` (${currencies[homeCode]})` : ""}. Pick another Market to see the ask; Developed / High population usually Stay.`}
              />
              <s-stack direction="inline" gap="small" alignItems="center">
                <s-text color="subdued">Preview in</s-text>
                <s-button
                  type="button"
                  variant={previewLang === "en" ? "primary" : "tertiary"}
                  onClick={() => setPreviewLang("en")}
                >
                  English
                </s-button>
                <s-button
                  type="button"
                  variant={previewLang === "fr" ? "primary" : "tertiary"}
                  onClick={() => setPreviewLang("fr")}
                >
                  Français
                </s-button>
              </s-stack>
              <s-text color="subdued">
                {msgFor === "market" ? "Market" : "Store"} popup ·{" "}
                {previewLabel}
                {previewCurrency ? ` · ${previewCurrency}` : ""} ·{" "}
                {previewLang === "fr"
                  ? "built-in French translation"
                  : "your wording"}{" "}
                · updates live
              </s-text>
              <PopupPreview
                style={previewStyle}
                title={previewTitle}
                message={previewMessage}
                helper={helperPreview}
                button={previewGo}
                buttonColor={draft.buttonColor}
                device={device}
                buttonRadius={draft.buttonStyle === "square" ? 0 : 8}
                titleSize={draft.titleSize}
                textSize={draft.textSize}
                showBlur={draft.showBlur}
                buttonAlign={draft.buttonAlign}
                chooser={msgFor === "market"}
                countries={chooserCountries}
                languages={chooserLanguages}
                selectedCountry={
                  previewInMarkets ? previewCode.toUpperCase() : askPreviewCode
                }
                selectedLanguage={previewLang}
                showCurrency={draft.showCurrency}
                showLanguage={draft.showLanguage}
                showFlags={draft.showFlags}
                currencies={currencies}
              />
              {!embedOn ? (
                <WarningBanner heading="Turn on the theme embed first">
                  Open in store only works after SafeGeo is enabled in the theme.{" "}
                  {embedEditorUrl ? (
                    <AppLink href={embedEditorUrl}>Open theme editor</AppLink>
                  ) : null}
                </WarningBanner>
              ) : null}
              {!canOpenStore ? (
                <WarningBanner heading="Storefront preview unavailable">
                  Need SAFEGEO_WORKER_URL or API_PUBLIC_URL, then Publish. Until
                  then the storefront falls back to your real IP.
                </WarningBanner>
              ) : null}
              <s-paragraph color="subdued">
                {openStoreNoAsk
                  ? `Opening as ${previewLabel} won’t show the ask popup. Counts under Home → Test Mode (not Live Mode).`
                  : `Opens your storefront as ${previewLabel}. Look for a black “Previewing as…” bar and the ask popup. Counts under Home → Test Mode (not Live Mode).`}
              </s-paragraph>
              {previewIsHome ? (
                <WarningBanner heading="This country is your primary market">
                  Opening as {previewLabel} won’t show the ask (already home).
                  Preview {askPreviewLabel} instead to see the popup.
                </WarningBanner>
              ) : !previewInMarkets ? (
                <InfoBanner heading="Not in your Shopify Markets">
                  {previewLabel} isn’t a Market country, so SafeGeo stays quiet
                  (no ask). That’s expected for Developed / high-population
                  checks. Preview {askPreviewLabel} to see the ask popup.
                </InfoBanner>
              ) : null}
              <AppButton
                variant="secondary"
                disabled={
                  !embedOn ||
                  busy ||
                  !shop ||
                  openStoreNoAsk ||
                  !canOpenStore
                }
                onClick={() => void openStorePreview()}
              >
                Open storefront as {previewLabel}
                {previewCurrency ? ` (${previewCurrency})` : ""} visitor
              </AppButton>
            </s-stack>
          </s-box>
        </div>

        <div className="sg-popup-form">
          <s-stack gap="base">
            <s-section>
              <s-stack direction="inline" gap="small">
                {(
                  [
                    ["content", "Content"],
                    ["selector", "Selector"],
                    ["style", "Style"],
                  ] as const
                ).map(([id, label]) => (
                  <s-button
                    key={id}
                    type="button"
                    variant={settingsTab === id ? "primary" : "tertiary"}
                    onClick={() => setSettingsTab(id)}
                  >
                    {label}
                  </s-button>
                ))}
              </s-stack>
            </s-section>

            {settingsTab === "content" ? (
              <>
                <s-section heading="Message for">
                  <s-stack gap="base">
                    <s-stack direction="inline" gap="small">
                      <s-button
                        type="button"
                        variant={msgFor === "market" ? "primary" : "secondary"}
                        onClick={() => {
                          if (msgFor === "store") {
                            setStoreCopy({
                              title: draft.title,
                              reason1: draft.reason1,
                              reason2: draft.reason2,
                              button: draft.button,
                            });
                            const m = designQ.data;
                            if (m) {
                              patch({
                                title: m.title || draft.title,
                                reason1: m.reason_1 || draft.reason1,
                                reason2: m.reason_2 || draft.reason2,
                                button: m.button || draft.button,
                              });
                            }
                          }
                          setMsgFor("market");
                        }}
                      >
                        Market
                      </s-button>
                      <s-button
                        type="button"
                        variant={msgFor === "store" ? "primary" : "secondary"}
                        onClick={() => {
                          if (msgFor === "market" && !storeCopy) {
                            setStoreCopy({
                              title: draft.title,
                              reason1: draft.reason1,
                              reason2: draft.reason2,
                              button: draft.button,
                            });
                          }
                          if (storeCopy) {
                            patch({
                              title: storeCopy.title,
                              reason1: storeCopy.reason1,
                              reason2: storeCopy.reason2,
                              button: storeCopy.button,
                            });
                          }
                          setMsgFor("store");
                        }}
                      >
                        Store
                      </s-button>
                    </s-stack>
                    <s-paragraph color="subdued">
                      Market and Store share one popup design. Each has its own
                      wording and button label.
                    </s-paragraph>
                  </s-stack>
                </s-section>

                <s-section heading="Text">
                  <s-stack gap="base">
                    <s-text-field
                      label="Title"
                      value={draft.title}
                      onChange={(e: Event) =>
                        patch({ title: fieldValue(e).slice(0, 80) })
                      }
                    />
                    <s-text-field
                      label="Supporting text"
                      value={draft.reason1}
                      onChange={(e: Event) =>
                        patch({ reason1: fieldValue(e).slice(0, 120) })
                      }
                    />
                    <s-text-field
                      label="Button label"
                      value={draft.button}
                      onChange={(e: Event) =>
                        patch({ button: fieldValue(e).slice(0, 40) })
                      }
                      details="One button. Visitors can also close with the X, which keeps them where they are."
                    />
                    <s-text-field
                      label="Helper text (optional)"
                      value={helperOn ? draft.reason2 : ""}
                      onChange={(e: Event) => {
                        const v = fieldValue(e).slice(0, 280);
                        setHelperOn(v.trim().length > 0);
                        patch({ reason2: v });
                      }}
                    />
                    <s-paragraph color="subdued">
                      Variables: {"{country}"} · {"{currency}"} ·{" "}
                      {"{current_currency}"} · {"{store}"}
                    </s-paragraph>
                    <s-banner tone="info">
                      Your wording is used for English, your main language.
                      Visitors on a page in another language see our built-in
                      translation. Use Preview in on the right to check it.
                    </s-banner>
                  </s-stack>
                </s-section>
              </>
            ) : null}

            {settingsTab === "selector" ? (
              <>
                <s-section heading="Message for">
                  <s-stack direction="inline" gap="small">
                    <s-button
                      type="button"
                      variant={msgFor === "market" ? "primary" : "secondary"}
                      onClick={() => setMsgFor("market")}
                    >
                      Market
                    </s-button>
                    <s-button
                      type="button"
                      variant={msgFor === "store" ? "primary" : "secondary"}
                      onClick={() => setMsgFor("store")}
                    >
                      Store
                    </s-button>
                  </s-stack>
                </s-section>
                {msgFor === "store" ? (
                  <s-banner tone="info">
                    The Store popup has no selector. Visitors tap the button to
                    go to the other store, or close it with the X to stay.
                  </s-banner>
                ) : (
                  <s-section heading="Selector">
                    <s-stack gap="base">
                      <s-paragraph color="subdued">
                        Visitors choose where they shop in the popup, then tap
                        the button. Their own country is preselected.
                      </s-paragraph>
                      <s-stack
                        direction="inline"
                        gap="base"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <s-stack gap="small-100">
                          <s-text type="strong">Country</s-text>
                          <s-paragraph color="subdued">
                            Always shown. Lists the countries of your Shopify
                            markets.
                          </s-paragraph>
                        </s-stack>
                        <s-badge>Always on</s-badge>
                      </s-stack>
                      <s-checkbox
                        label="Show currency"
                        checked={draft.showCurrency}
                        details="Shown next to the country, for example Canada · CAD."
                        onChange={(e: Event) =>
                          patch({ showCurrency: fieldChecked(e) })
                        }
                      />
                      <s-checkbox
                        label="Show language"
                        checked={draft.showLanguage}
                        details={
                          languageSelectHint
                            ? "Your store looks like it has one language. Add another in Shopify Markets, or turn this on after you publish a second language. The Language field only appears when the visitor’s market has 2+ languages."
                            : "Languages come from the visitor’s chosen market. Turn off to hide the Language field."
                        }
                        onChange={(e: Event) =>
                          patch({ showLanguage: fieldChecked(e) })
                        }
                      />
                      {languageSelectHint ? (
                        <s-paragraph color="subdued">
                          <AppLink href="https://admin.shopify.com/settings/markets">
                            Add a language in Shopify Markets
                          </AppLink>
                        </s-paragraph>
                      ) : null}
                      <s-checkbox
                        label="Show flags"
                        checked={draft.showFlags}
                        details="A small flag next to each country."
                        onChange={(e: Event) =>
                          patch({ showFlags: fieldChecked(e) })
                        }
                      />
                      <s-stack gap="small">
                        <s-text type="strong">Country order</s-text>
                        <s-stack direction="inline" gap="small">
                          <s-button
                            type="button"
                            variant={
                              draft.countryOrder === "alpha"
                                ? "primary"
                                : "secondary"
                            }
                            onClick={() => patch({ countryOrder: "alpha" })}
                          >
                            A to Z
                          </s-button>
                          <s-button
                            type="button"
                            variant={
                              draft.countryOrder === "market"
                                ? "primary"
                                : "secondary"
                            }
                            onClick={() => patch({ countryOrder: "market" })}
                          >
                            By market
                          </s-button>
                        </s-stack>
                        <s-paragraph color="subdued">
                          Their own country is always first. The rest follow
                          this order.
                        </s-paragraph>
                      </s-stack>
                    </s-stack>
                  </s-section>
                )}
              </>
            ) : null}

            {settingsTab === "style" ? (
              <>
                <s-section heading="Placement">
                  <s-stack gap="base">
                    <s-stack direction="inline" gap="small">
                      {PRESETS.map((s) => (
                        <s-button
                          key={s.id}
                          type="button"
                          variant={
                            draft.style === s.id ? "primary" : "secondary"
                          }
                          onClick={() =>
                            patch({
                              style: s.id,
                              showBlur: s.id === "center",
                            })
                          }
                        >
                          {s.label}
                        </s-button>
                      ))}
                    </s-stack>
                    <s-paragraph color="subdued">
                      {PRESETS.find((s) => s.id === draft.style)?.hint}
                    </s-paragraph>
                    <s-banner tone="info">
                      The close button (X) is always shown, so visitors can stay
                      where they are. Ask first or Automatic is set per country.
                      How long a choice is remembered is in Settings, Visitors.
                    </s-banner>
                  </s-stack>
                </s-section>

                <s-section heading="Colors">
                  <s-stack gap="base">
                    <s-stack direction="inline" gap="small">
                      {COLOR_PRESETS.map((c) => (
                        <s-button
                          key={c.id}
                          type="button"
                          variant={
                            draft.buttonColor.toLowerCase() ===
                            c.id.toLowerCase()
                              ? "primary"
                              : "secondary"
                          }
                          onClick={() =>
                            patch({
                              buttonColor: c.id,
                              matchTheme: false,
                            })
                          }
                        >
                          {c.label}
                        </s-button>
                      ))}
                    </s-stack>
                    <s-text-field
                      label="Interactive / button color"
                      value={draft.buttonColor}
                      onChange={(e: Event) =>
                        patch({
                          buttonColor: fieldValue(e),
                          matchTheme: false,
                        })
                      }
                    />
                  </s-stack>
                </s-section>

                <s-section heading="Button style">
                  <s-stack direction="inline" gap="small">
                    <s-button
                      type="button"
                      variant={
                        draft.buttonStyle === "square"
                          ? "primary"
                          : "secondary"
                      }
                      onClick={() => patch({ buttonStyle: "square" })}
                    >
                      Square
                    </s-button>
                    <s-button
                      type="button"
                      variant={
                        draft.buttonStyle === "rounded"
                          ? "primary"
                          : "secondary"
                      }
                      onClick={() => patch({ buttonStyle: "rounded" })}
                    >
                      Rounded
                    </s-button>
                  </s-stack>
                </s-section>

                <s-section heading="Custom CSS">
                  <s-stack gap="small">
                    <s-stack direction="inline" gap="small" alignItems="center">
                      <s-text type="strong">Custom CSS</s-text>
                      {!canCss ? <s-badge>Starter+</s-badge> : null}
                    </s-stack>
                    <s-paragraph color="subdued">
                      Advanced. Try the options above first.
                    </s-paragraph>
                    {canCss ? (
                      <s-text-area
                        label="Custom CSS"
                        value={draft.css}
                        rows={4}
                        details=".sg-popup { border-radius: 4px; }"
                        onChange={(e: Event) =>
                          patch({ css: fieldValue(e).slice(0, 10000) })
                        }
                      />
                    ) : (
                      <UpgradeNowButton plan="starter" />
                    )}
                    {canCss ? (
                      <s-paragraph color="subdued">{plan}</s-paragraph>
                    ) : null}
                  </s-stack>
                </s-section>
              </>
            ) : null}
          </s-stack>
        </div>
      </div>

    </SafePage>
  );
}
